<script setup>
import { ref, watch, computed } from 'vue'

const props = defineProps({
  notifications: {
    style: Array,
    default: () => [],
  },
})
const visible = ref([])

const getDismissed = () => {
  const cookie = document.cookie
    .split('; ')
    .find(row => row.startsWith('dismissed_notifications='))
  return cookie ? cookie.split('=')[1].split(',') : []
}

const initializeVisibility = () => {
  const dismissed = getDismissed()

  visible.value = props.notifications
    .filter(n => !dismissed.includes(String(n.id)))
    .map(n => ({
      ...n,
      _visible: false,
      _expanded: false,
    }))

  setTimeout(() => {
    visible.value = visible.value.map(n => ({
      ...n,
      _visible: true,
    }))
  }, 50)
}

const dismiss = id => {
  const n = visible.value.find(n => n.id === id)
  if (n) n._visible = false

  const dismissed = getDismissed()
  if (!dismissed.includes(String(id))) {
    dismissed.push(String(id))
    document.cookie = `dismissed_notifications=${dismissed.join(',')}; path=/; max-age=${60 * 60 * 24 * 30}`
  }
}

watch(
  () => props.notifications,
  () => {
    initializeVisibility()
  },
  { immediate: true, deep: true }
)

const toggleExpand = id => {
  const n = visible.value.find(n => n.id === id)
  if (n) n._expanded = !n._expanded
}

const visibleNotifications = computed(() =>
  visible.value.filter(n => n._visible)
)

const styleClasses = {
  info: 'bg-blue-50 border-blue-500 text-blue-800',
  warning: 'bg-yellow-50 border-yellow-500 text-yellow-800',
  error: 'bg-red-50 border-red-500 text-red-800',
  success: 'bg-green-50 border-green-500 text-green-800',
  default: 'bg-gray-50 border-gray-500 text-gray-800',
}
</script>

<template>
  <div
    v-if="visibleNotifications.length"
    class="top-4 inset-x-0 px-0 mx-auto max-w-xl sm:px-6 xl:px-8 space-y-4"
  >
    <TransitionGroup
      tag="div"
      enter-from="opacity-0 translate-y-4"
      enter-active="transition ease-out duration-500"
      enter-to="opacity-100 translate-y-0"
      leave-from="opacity-100 translate-y-0"
      leave-active="transition ease-in duration-400"
      leave-to="opacity-0 translate-y-4"
    >
      <div
        v-for="(notification, index) in visibleNotifications"
        :key="notification.id"
        :class="[
          'pointer-events-auto relative rounded-lg p-4 shadow-md border-l-4 transition-all duration-300 mb-4 max-h-[60vh] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-transparent',
          styleClasses[notification.style] || styleClasses.default]"
        role="alert"
        aria-live="assertive"
      >
        <button
          :dusk="`close-notification-${index}`"
          @click="dismiss(notification.id)"
          aria-label="Close notification"
          class="absolute top-3 right-3 flex items-center justify-center w-8 h-8 rounded-full bg-gray-200 hover:bg-gray-300 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-400"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <strong class="block mb-1">{{ notification.title }}</strong>

        <span v-html="notification._expanded || notification.message.split(' ').length <= 30
          ? notification.message
          : notification.message.split(' ').slice(0, 30).join(' ') + '...'">
        </span>

        <div v-if="notification.message.split(' ').length > 30" class="mt-2 text-right">
          <button
            @click="toggleExpand(notification.id)"
            class="text-sm text-gray-600 hover:underline"
          >
            {{ notification._expanded ? 'Show less' : 'Show more' }}
          </button>
        </div>
      </div>
    </TransitionGroup>
  </div>
</template>
